/**
 * Quick View Modal - Tienda de Calzado Deportivo Colombia
 *
 * Custom element que extiende ModalDialog para cargar productos
 * via AJAX y permitir agregar al carrito sin salir de la pagina.
 */

if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickViewContent-"]');
        this.productData = null;
        this.selectedVariantId = null;
        this.quantity = 1;
      }

      /**
       * Muestra el modal y carga el contenido del producto via AJAX
       * @param {HTMLElement} opener - Elemento que abrio el modal
       */
      show(opener) {
        // Marcar boton como cargando
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const productUrl = opener.getAttribute('data-product-url');

        // Fetch del producto
        fetch(`${productUrl}?view=quick-view-data`)
          .then((response) => {
            // Si no existe la vista especial, usar la URL normal
            if (!response.ok) {
              return fetch(productUrl);
            }
            return response;
          })
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.extractProductData(responseHTML, productUrl);
            this.renderProductContent();
            super.show(opener);
          })
          .catch((error) => {
            console.error('Error loading Quick View:', error);
            this.renderErrorState();
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      /**
       * Extrae los datos del producto del HTML de respuesta
       * @param {Document} responseHTML - Documento HTML parseado
       * @param {string} productUrl - URL del producto
       */
      extractProductData(responseHTML, productUrl) {
        // Buscar datos del producto en diferentes selectores posibles
        const productInfo = responseHTML.querySelector('product-info') ||
                           responseHTML.querySelector('[data-product-info]') ||
                           responseHTML.querySelector('.product');

        // Extraer imagen principal
        const mainImage = responseHTML.querySelector('.product__media img') ||
                         responseHTML.querySelector('[data-product-featured-image]') ||
                         responseHTML.querySelector('.product-media-container img');

        // Extraer todas las imagenes
        const allImages = responseHTML.querySelectorAll('.product__media img, .thumbnail-list img, [data-thumbnail]');

        // Extraer titulo
        const titleElement = responseHTML.querySelector('.product__title, h1.title, [data-product-title]');

        // Extraer vendor
        const vendorElement = responseHTML.querySelector('.product__text, [data-vendor], .vendor');

        // Extraer precio
        const priceElement = responseHTML.querySelector('.price__regular .price-item, .price-item--regular, [data-product-price]');
        const comparePriceElement = responseHTML.querySelector('.price__sale .price-item--regular, .price-item--compare, [data-compare-price]');

        // Extraer rating
        const ratingElement = responseHTML.querySelector('.rating, [data-product-rating]');

        // Extraer variantes del JSON
        let variants = [];
        const variantJson = responseHTML.querySelector('[type="application/json"][data-product-variants], script#ProductJson-product-template, [data-product-json]');

        if (variantJson) {
          try {
            const jsonData = JSON.parse(variantJson.textContent);
            variants = jsonData.variants || jsonData;
          } catch (e) {
            console.warn('Could not parse variant JSON');
          }
        }

        // Si no hay variantes en JSON, buscar en los selectores
        if (variants.length === 0) {
          const variantOptions = responseHTML.querySelectorAll('[data-variant-id], .product-form__input input[type="radio"]');
          variantOptions.forEach(option => {
            const variantId = option.dataset.variantId || option.value;
            const variantName = option.dataset.optionValue || option.nextElementSibling?.textContent?.trim();
            if (variantId) {
              variants.push({
                id: parseInt(variantId),
                title: variantName || 'Variante',
                available: !option.disabled,
                price: 0
              });
            }
          });
        }

        // Obtener la variante seleccionada o la primera disponible
        const selectedVariant = variants.find(v => v.available) || variants[0];

        this.productData = {
          url: productUrl,
          title: titleElement?.textContent?.trim() || 'Producto',
          vendor: vendorElement?.textContent?.trim() || '',
          mainImage: mainImage?.src || '',
          mainImageAlt: mainImage?.alt || '',
          images: Array.from(allImages).map(img => ({
            src: img.src,
            alt: img.alt || ''
          })).filter((img, index, self) =>
            index === self.findIndex(i => i.src === img.src)
          ).slice(0, 5),
          price: this.extractPrice(priceElement),
          comparePrice: this.extractPrice(comparePriceElement),
          rating: this.extractRating(ratingElement),
          variants: variants,
          selectedVariant: selectedVariant,
          available: selectedVariant?.available ?? true
        };

        this.selectedVariantId = selectedVariant?.id;
      }

      /**
       * Extrae el precio numerico desde un elemento del DOM
       * @param {Element} element - Elemento con el precio formateado (ej: "$150.000")
       * @returns {number} Precio como numero entero (ej: 150000)
       */
      extractPrice(element) {
        if (!element) return 0;
        const text = element.textContent.replace(/[^\d]/g, '');
        return parseInt(text) || 0;
      }

      /**
       * Extrae el rating del producto
       * @param {Element} element - Elemento con el rating
       * @returns {Object} Datos del rating
       */
      extractRating(element) {
        if (!element) return null;
        const ratingValue = element.querySelector('.rating-star')?.style.getPropertyValue('--rating') ||
                          element.getAttribute('data-rating');
        const ratingCount = element.querySelector('.rating-count')?.textContent?.match(/\d+/)?.[0];

        if (!ratingValue) return null;

        return {
          value: parseFloat(ratingValue) || 0,
          count: parseInt(ratingCount) || 0
        };
      }

      /**
       * Formatea un precio para Colombia
       * @param {number} price - Precio numerico (ya en valor real, no centavos)
       * @returns {string} Precio formateado
       */
      formatPrice(price) {
        if (!price) return '$0';
        // El precio viene del DOM ya formateado y se extrae como numero entero
        // Por ejemplo: "$150.000" -> 150000 (ya es el valor real en COP)
        return new Intl.NumberFormat('es-CO', {
          style: 'currency',
          currency: 'COP',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(price);
      }

      /**
       * Renderiza el contenido del producto en el modal
       */
      renderProductContent() {
        if (!this.productData) {
          this.renderErrorState();
          return;
        }

        const data = this.productData;
        const hasDiscount = data.comparePrice && data.comparePrice > data.price;
        const discountPercent = hasDiscount
          ? Math.round((1 - data.price / data.comparePrice) * 100)
          : 0;

        // Generar HTML de thumbnails
        const thumbnailsHTML = data.images.length > 1
          ? data.images.map((img, index) => `
              <img
                src="${img.src}"
                alt="${img.alt}"
                class="quick-view-product__thumbnail ${index === 0 ? 'active' : ''}"
                data-index="${index}"
                loading="lazy"
              >
            `).join('')
          : '';

        // Generar HTML de rating
        const ratingHTML = data.rating ? `
          <div class="quick-view-product__rating">
            <div class="quick-view-product__stars">
              ${this.generateStarsHTML(data.rating.value)}
            </div>
            <span class="quick-view-product__rating-count">(${data.rating.count} resenas)</span>
          </div>
        ` : '';

        // Generar HTML de variantes (tallas)
        const variantsHTML = data.variants.length > 1 ? `
          <div class="quick-view-product__variants">
            <span class="quick-view-product__variant-label">Talla:</span>
            <div class="quick-view-product__variant-pills">
              ${data.variants.map(variant => `
                <button
                  type="button"
                  class="quick-view-product__variant-pill ${variant.id === this.selectedVariantId ? 'selected' : ''} ${!variant.available ? 'unavailable' : ''}"
                  data-variant-id="${variant.id}"
                  ${!variant.available ? 'disabled' : ''}
                >
                  ${variant.title || variant.option1 || 'Talla'}
                </button>
              `).join('')}
            </div>
          </div>
        ` : '';

        // Disponibilidad
        const availabilityHTML = data.available
          ? '<p class="quick-view-product__availability quick-view-product__availability--in-stock">En stock - Listo para enviar</p>'
          : '<p class="quick-view-product__availability quick-view-product__availability--out-of-stock">Agotado</p>';

        const html = `
          <div class="quick-view-product">
            <!-- Galeria de imagenes -->
            <div class="quick-view-product__gallery">
              <img
                src="${data.mainImage}"
                alt="${data.mainImageAlt}"
                class="quick-view-product__main-image"
                id="QuickViewMainImage-${this.id}"
              >
              ${thumbnailsHTML ? `
                <div class="quick-view-product__thumbnails">
                  ${thumbnailsHTML}
                </div>
              ` : ''}
            </div>

            <!-- Info del producto -->
            <div class="quick-view-product__info">
              ${data.vendor ? `<p class="quick-view-product__vendor">${data.vendor}</p>` : ''}

              <h2 class="quick-view-product__title">${data.title}</h2>

              ${ratingHTML}

              <div class="quick-view-product__price">
                <span class="quick-view-product__price-current">${this.formatPrice(data.price)}</span>
                ${hasDiscount ? `
                  <span class="quick-view-product__price-compare">${this.formatPrice(data.comparePrice)}</span>
                  <span class="quick-view-product__price-badge">-${discountPercent}%</span>
                ` : ''}
              </div>

              ${availabilityHTML}

              ${variantsHTML}

              <!-- Cantidad -->
              <div class="quick-view-product__quantity">
                <span class="quick-view-product__quantity-label">Cantidad:</span>
                <div class="quick-view-product__quantity-selector">
                  <button
                    type="button"
                    class="quick-view-product__quantity-btn"
                    data-action="decrease"
                    ${this.quantity <= 1 ? 'disabled' : ''}
                  >-</button>
                  <input
                    type="number"
                    class="quick-view-product__quantity-input"
                    value="${this.quantity}"
                    min="1"
                    max="10"
                  >
                  <button
                    type="button"
                    class="quick-view-product__quantity-btn"
                    data-action="increase"
                  >+</button>
                </div>
              </div>

              <!-- Mensaje de error -->
              <div class="quick-view-product__error" id="QuickViewError-${this.id}"></div>

              <!-- Acciones -->
              <div class="quick-view-product__actions">
                <button
                  type="button"
                  class="quick-view-product__add-to-cart"
                  ${!data.available ? 'disabled' : ''}
                >
                  <span class="quick-view-product__add-to-cart-text">
                    ${data.available ? 'Agregar al Carrito' : 'Agotado'}
                  </span>
                  <svg class="loading__spinner" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="30 70" transform="rotate(-90 12 12)">
                      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                    </circle>
                  </svg>
                </button>

                <a href="${data.url}" class="quick-view-product__view-details">
                  Ver detalles completos
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        `;

        this.modalContent.innerHTML = html;
        this.bindEvents();
      }

      /**
       * Genera el HTML de las estrellas de rating
       * @param {number} rating - Valor del rating (0-5)
       * @returns {string} HTML de las estrellas
       */
      generateStarsHTML(rating) {
        let starsHTML = '';
        for (let i = 1; i <= 5; i++) {
          const filled = i <= Math.floor(rating);
          const halfFilled = !filled && i === Math.ceil(rating) && rating % 1 !== 0;

          if (filled) {
            starsHTML += `
              <svg class="quick-view-product__star" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
              </svg>
            `;
          } else if (halfFilled) {
            starsHTML += `
              <svg class="quick-view-product__star" viewBox="0 0 24 24">
                <defs>
                  <linearGradient id="half-star">
                    <stop offset="50%" stop-color="currentColor"/>
                    <stop offset="50%" stop-color="#D0D0D0"/>
                  </linearGradient>
                </defs>
                <path fill="url(#half-star)" d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
              </svg>
            `;
          } else {
            starsHTML += `
              <svg class="quick-view-product__star quick-view-product__star--empty" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
              </svg>
            `;
          }
        }
        return starsHTML;
      }

      /**
       * Renderiza estado de error
       */
      renderErrorState() {
        this.modalContent.innerHTML = `
          <div class="quick-view-modal__loading">
            <p>No se pudo cargar el producto. Por favor, intenta de nuevo.</p>
            <a href="${this.productData?.url || '#'}" class="quick-view-product__view-details" style="margin-top: 16px;">
              Ver producto
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          </div>
        `;
      }

      /**
       * Vincula eventos del modal
       */
      bindEvents() {
        // Thumbnails
        const thumbnails = this.querySelectorAll('.quick-view-product__thumbnail');
        const mainImage = this.querySelector('.quick-view-product__main-image');

        thumbnails.forEach(thumb => {
          thumb.addEventListener('click', () => {
            thumbnails.forEach(t => t.classList.remove('active'));
            thumb.classList.add('active');
            if (mainImage) {
              mainImage.src = thumb.src;
              mainImage.alt = thumb.alt;
            }
          });
        });

        // Variantes
        const variantPills = this.querySelectorAll('.quick-view-product__variant-pill');
        variantPills.forEach(pill => {
          pill.addEventListener('click', () => {
            if (pill.disabled) return;

            variantPills.forEach(p => p.classList.remove('selected'));
            pill.classList.add('selected');
            this.selectedVariantId = parseInt(pill.dataset.variantId);

            // Actualizar imagen si la variante tiene imagen asociada
            const variant = this.productData.variants.find(v => v.id === this.selectedVariantId);
            if (variant?.featured_image?.src && mainImage) {
              mainImage.src = variant.featured_image.src;
            }
          });
        });

        // Cantidad
        const quantityInput = this.querySelector('.quick-view-product__quantity-input');
        const decreaseBtn = this.querySelector('[data-action="decrease"]');
        const increaseBtn = this.querySelector('[data-action="increase"]');

        if (decreaseBtn) {
          decreaseBtn.addEventListener('click', () => {
            if (this.quantity > 1) {
              this.quantity--;
              quantityInput.value = this.quantity;
              decreaseBtn.disabled = this.quantity <= 1;
            }
          });
        }

        if (increaseBtn) {
          increaseBtn.addEventListener('click', () => {
            if (this.quantity < 10) {
              this.quantity++;
              quantityInput.value = this.quantity;
              decreaseBtn.disabled = false;
            }
          });
        }

        if (quantityInput) {
          quantityInput.addEventListener('change', (e) => {
            let value = parseInt(e.target.value);
            if (isNaN(value) || value < 1) value = 1;
            if (value > 10) value = 10;
            this.quantity = value;
            quantityInput.value = value;
            if (decreaseBtn) decreaseBtn.disabled = this.quantity <= 1;
          });
        }

        // Agregar al carrito
        const addToCartBtn = this.querySelector('.quick-view-product__add-to-cart');
        if (addToCartBtn) {
          addToCartBtn.addEventListener('click', () => this.addToCart());
        }
      }

      /**
       * Agrega el producto al carrito via Shopify Cart API
       */
      async addToCart() {
        const addToCartBtn = this.querySelector('.quick-view-product__add-to-cart');
        const errorDiv = this.querySelector('.quick-view-product__error');

        if (!this.selectedVariantId) {
          this.showError('Por favor selecciona una talla');
          return;
        }

        // Estado de carga
        addToCartBtn.classList.add('loading');
        addToCartBtn.disabled = true;
        errorDiv?.classList.remove('show');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: this.selectedVariantId,
              quantity: this.quantity
            })
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.description || 'Error al agregar al carrito');
          }

          // Exito - mostrar feedback
          addToCartBtn.classList.remove('loading');
          addToCartBtn.classList.add('success');
          addToCartBtn.querySelector('.quick-view-product__add-to-cart-text').textContent = 'Agregado!';

          // Actualizar contador del carrito
          this.updateCartCount();

          // Disparar evento para cart drawer o notificacion
          document.dispatchEvent(new CustomEvent('cart:item-added', {
            detail: { product: result }
          }));

          // Resetear boton despues de 2 segundos
          setTimeout(() => {
            addToCartBtn.classList.remove('success');
            addToCartBtn.querySelector('.quick-view-product__add-to-cart-text').textContent = 'Agregar al Carrito';
            addToCartBtn.disabled = false;
          }, 2000);

        } catch (error) {
          console.error('Error adding to cart:', error);
          addToCartBtn.classList.remove('loading');
          addToCartBtn.disabled = false;
          this.showError(error.message || 'No se pudo agregar al carrito. Intenta de nuevo.');
        }
      }

      /**
       * Muestra un mensaje de error
       * @param {string} message - Mensaje de error
       */
      showError(message) {
        const errorDiv = this.querySelector('.quick-view-product__error');
        if (errorDiv) {
          errorDiv.textContent = message;
          errorDiv.classList.add('show');

          setTimeout(() => {
            errorDiv.classList.remove('show');
          }, 5000);
        }
      }

      /**
       * Actualiza el contador del carrito en el header
       */
      async updateCartCount() {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();

          // Actualizar bubble del carrito
          const cartBubble = document.querySelector('.cart-count-bubble');
          const cartCount = document.querySelector('[data-cart-count]');

          if (cartBubble) {
            const countElement = cartBubble.querySelector('span');
            if (countElement) {
              countElement.textContent = cart.item_count;
            }
            cartBubble.classList.toggle('hidden', cart.item_count === 0);
          }

          if (cartCount) {
            cartCount.textContent = cart.item_count;
          }

          // Intentar abrir cart drawer si existe
          const cartDrawer = document.querySelector('cart-drawer');
          const cartNotification = document.querySelector('cart-notification');

          if (cartDrawer && typeof cartDrawer.open === 'function') {
            // El cart drawer existe, disparar evento
            fetch('/cart?sections=cart-drawer,cart-icon-bubble')
              .then(res => res.json())
              .then(sections => {
                document.dispatchEvent(new CustomEvent('cart:refresh', {
                  detail: { sections }
                }));
              });
          } else if (cartNotification) {
            // Mostrar notificacion de carrito
            cartNotification.renderContents?.(cart);
          }

        } catch (error) {
          console.error('Error updating cart count:', error);
        }
      }

      /**
       * Oculta el modal y limpia el contenido
       * @param {boolean} preventFocus - Evitar restaurar el foco
       */
      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement?.(this.openedBy);

        // Limpiar contenido
        if (this.modalContent) {
          this.modalContent.innerHTML = `
            <div class="quick-view-modal__loading">
              <div class="quick-view-modal__spinner">
                <svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle class="spinner-path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
              <p>Cargando producto...</p>
            </div>
          `;
        }

        // Resetear estado
        this.productData = null;
        this.selectedVariantId = null;
        this.quantity = 1;

        if (preventFocus) this.openedBy = null;
        super.hide();
      }
    }
  );
}
